import Form from "@rjsf/react-bootstrap";
import React, { FunctionComponent, useEffect } from "react";
import { JSONSchema7 } from "json-schema";
import { Link, useNavigate, useLocation } from "react-router";
import { toast } from "react-toastify";
import firebase from "firebase/compat/app";
import validator from "@rjsf/validator-ajv8";
import { UiSchema } from "@rjsf/utils";
import { Helmet } from "react-helmet";

import { firebaseAppAuth } from "../../lib/firebase";
import { trackEvent, trackFormEvent } from "../../helper/analytics";
import { trackPlausibleGoal, PlausibleGoals } from "../../helper/plausibleAnalytics";
import { AuthLayout } from "./AuthLayout";
import FirebaseService from "../../actions/firebase/FirebaseActions";
import { useUserContext } from "../../contexts/UserContext";

const schema: JSONSchema7 = {
  type: "object",
  required: ["email", "password"],
  properties: {
    email: {
      type: "string",
      title: "E-Mail"
    },
    password: {
      type: "string",
      title: "Passwort"
    }
  }
};

const uiSchema: UiSchema = {
  email: {
    "ui:widget": "email"
  },
  password: {
    "ui:widget": "password"
  }
};

interface Props {
  user: firebase.User | null | undefined;
}

const SignIn: FunctionComponent<Props> = ({ user }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveUser } = useUserContext();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const searchParams = new URLSearchParams(location.search);
    const nextPage = searchParams.get("nextPage");

    if (nextPage) {
      navigate(decodeURIComponent(nextPage));
    } else {
      FirebaseService.getUserDefaultForm(user.uid)
        .then(async (defaultForm) => {
          if (cancelled) return;
          const formOwnerUser = await FirebaseService.getUser(defaultForm.formOwnerId);
          if (cancelled) return;
          setActiveUser(formOwnerUser);
          navigate(`/user/${formOwnerUser.id}/dashboard`);
        })
        .catch(async () => {
          if (cancelled) return;
          try {
            await FirebaseService.setDefaultFormOwner(user.uid, user.uid);
            if (cancelled) return;
            const ownUser = await FirebaseService.getUser(user.uid);
            if (cancelled) return;
            setActiveUser(ownUser);
            navigate(`/user/${ownUser.id}/dashboard`);
          } catch (error) {
            if (cancelled) return;
            console.error("Error creating default form:", error);
            navigate(`/user/${user.uid}/dashboard`);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [user, navigate, location.search, setActiveUser]);

  const submitSignIn = async (formData: any): Promise<void> => {
    const { email, password } = formData;
    trackFormEvent("started", "login-form");

    try {
      if (firebaseAppAuth) {
        await firebaseAppAuth.signInWithEmailAndPassword(email, password);
        trackEvent("user_login", {
          method: "email_password",
          timestamp: new Date().toISOString()
        });
        trackFormEvent("completed", "login-form");
        trackPlausibleGoal(PlausibleGoals.USER_LOGIN);
      }
    } catch (error) {
      console.warn(error);
      trackEvent("login_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        timestamp: new Date().toISOString()
      });
      trackFormEvent("error", "login-form", {
        error: error instanceof Error ? error.message : "Authentication failed"
      });
      toast.error("Anmeldedaten sind nicht korrekt. Bitte versuchen Sie es erneut.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Spielgruppe Online - Anmelden</title>
        <meta name="description" content="Melden Sie sich an, um Ihre Spielgruppe-Anmeldungen zu verwalten" />
      </Helmet>
      <AuthLayout
        title={"Willkommen zurück"}
        lead={"Melden Sie sich an, um Ihre Anmeldungen zu verwalten"}
        footer={
          <>
            <p>
              Noch kein Konto? <Link to="/signup">Jetzt registrieren</Link>
            </p>
            <p>
              <Link to="/reset-password">Passwort vergessen?</Link>
            </p>
          </>
        }>
        <Form
          validator={validator}
          schema={schema}
          uiSchema={uiSchema}
          onSubmit={(e) => submitSignIn(e.formData)}
          className="login-form">
          <button type="submit" className="btn btn-primary btn-lg">
            Anmelden
          </button>
        </Form>
      </AuthLayout>
    </>
  );
};

export default SignIn;
